import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import CommissionContainer from './CommissionContainer';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import { Table, TableBody, TableCell, TableHead, TableRow, Grid } from '@mui/material';
import { Button, CollapseCard, Typography } from '@totalsoft/rocket-ui';
import { compose } from 'redux';
import { RightsContext } from 'providers/RightsContext';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { getErrors } from '@totalsoft/pure-validations-react';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import WrappedFakeText from 'components/common/WrappedFakeText';

const BaseCommissionsSubsidiesComponent = props => {
  const {
    t,
    commissions,
    numberOfCommissions,
    numberOfSubsidies,
    onPropertyChange,
    saving,
    listIsDirty,
    visibleColumns,
    calcInProgress,
    expandedSubsidyCard,
    expandedCommissionsCard,
    setExpandedSubsidyCard,
    setExpandedCommissionsCard,
    validation,
    loaded
  } = props;
  const { onSave, onAdd, onRemove, isSubsidy } = props;
  const { isImage, readOnlyFull, leasingCommissionsWrite, addLeasingCommission, offerSubsidiesWrite, addDeleteOfferSubsidies } =
    useContext(RightsContext);
  const cardName = isSubsidy ? 'subsidiesCard' : 'commissionsCard';
  const [commissionsLoading, setCommissionsLoading] = useState(true);

  useEffect(() => {
    setCommissionsLoading(!loaded);
  }, [loaded]);
  const getEditRight = useCallback(
    isSubsidy => (isSubsidy ? offerSubsidiesWrite && !readOnlyFull : leasingCommissionsWrite && !readOnlyFull),
    [leasingCommissionsWrite, offerSubsidiesWrite, readOnlyFull]
  );

  const getAddOrDeleteRight = useCallback(
    isSubsidy => (isSubsidy ? addDeleteOfferSubsidies && !readOnlyFull : addLeasingCommission && !readOnlyFull),
    [addDeleteOfferSubsidies, addLeasingCommission, readOnlyFull]
  );

  const cardExpanded = useMemo(
    () => (isSubsidy ? expandedSubsidyCard : expandedCommissionsCard),
    [expandedCommissionsCard, expandedSubsidyCard, isSubsidy]
  );

  const onExpand = useCallback(
    (_, expanded) => {
      isSubsidy ? setExpandedSubsidyCard(!expanded) : setExpandedCommissionsCard(!expanded);
    },
    [isSubsidy, setExpandedCommissionsCard, setExpandedSubsidyCard]
  );

  return (
    <CollapseCard
      id={cardName}
      icon={EuroSymbol}
      onToggle={onExpand}
      expanded={cardExpanded}
      title={isSubsidy ? t('OfferEdit.Subsidies.Title') : t('OfferEdit.Commissions.Title')}
      toggleOnHeaderClick
      subheader={
        <Grid item xs={12}>
          {((isSubsidy && offerSubsidiesWrite && numberOfSubsidies === 0) ||
            (!isSubsidy && leasingCommissionsWrite && numberOfCommissions === 0)) && <div>{t('ResultsNotFound')}</div>}
        </Grid>
      }
      hideSubheaderOnExpand
    >
      <>
        <Prompt when={commissions && commissions.some(x => x.dirtyInfo.isDirty)} message={t('LeavingWithoutSaving')} />
        <Grid container>
          <Grid item xs={12}>
            <CardNoResultsWrapper content={commissions}>
              <Table>
                <TableHead>
                  <TableRow>
                    {visibleColumns.includes('Name') && (
                      <TableCell>
                        <strong>{isSubsidy ? t('OfferEdit.Subsidies.SubsidyType') : t('OfferEdit.Commissions.CommissionType')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('CalculationName') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.CalculationMethod')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('Percent') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.Percent')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('Amount') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.Amount')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('Currency') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.Currency')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('PartnerName') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.Partner')}</strong>
                      </TableCell>
                    )}
                    {visibleColumns.includes('FreeTxt') && (
                      <TableCell>
                        <strong>{t('OfferEdit.Commissions.Notes')}</strong>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commissionsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <WrappedFakeText lines={2} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    commissions &&
                    commissions.map((item, idx) => (
                      <CommissionContainer
                        commission={item.document}
                        key={idx}
                        index={idx}
                        onPropertyChange={onPropertyChange}
                        onRemove={onRemove}
                        validationInfo={item.validationInfo}
                        cardName={cardName}
                        isSubsidy={isSubsidy}
                        visibleColumns={visibleColumns}
                        editRight={getEditRight(isSubsidy)}
                        addOrDeleteRight={getAddOrDeleteRight(isSubsidy)}
                        calcInProgress={calcInProgress}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </CardNoResultsWrapper>
          </Grid>
          {!isImage && (
            <>
              <GridActionsWrapper container item xs={12} mt={2}>
                <Grid item>
                  <Button
                    id={cardName + '_addBtn'}
                    onClick={onAdd}
                    disabled={!getEditRight(isSubsidy) || !getAddOrDeleteRight(isSubsidy) || calcInProgress.inProgress}
                  >
                    {t('Add')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    id={cardName + '_saveBtn'}
                    onClick={onSave}
                    disabled={
                      saving ||
                      !((commissions && commissions.some(x => x.dirtyInfo.isDirty)) || listIsDirty) ||
                      !getEditRight(isSubsidy) ||
                      calcInProgress.inProgress ||
                      (validation && (validation |> getErrors) !== '')
                    }
                  >
                    {saving ? t('Saving') + '...' : t('Save')}
                  </Button>
                </Grid>
              </GridActionsWrapper>
              {validation && (
                <Grid item textAlign='end'>
                  <Typography color='error'>{validation |> getErrors}</Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </>
    </CollapseCard>
  );
};

BaseCommissionsSubsidiesComponent.defaultProps = {
  visibleColumns: ['Name', 'CalculationName', 'Percent', 'Amount', 'Currency', 'PartnerName', 'FreeTxt']
};

BaseCommissionsSubsidiesComponent.propTypes = {
  commissions: PropTypes.array,
  numberOfCommissions: PropTypes.number,
  numberOfSubsidies: PropTypes.number,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  listIsDirty: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  isSubsidy: PropTypes.bool.isRequired,
  visibleColumns: PropTypes.array.isRequired,
  calcInProgress: PropTypes.bool.isRequired,
  expandedSubsidyCard: PropTypes.bool,
  setExpandedSubsidyCard: PropTypes.func,
  expandedCommissionsCard: PropTypes.bool,
  setExpandedCommissionsCard: PropTypes.func,
  validation: PropTypes.object,
  loaded: PropTypes.bool.isRequired
};

/**
 *
 * BaseCommissionsSubsidiesComponent is base component for CommissionsComponent and SubsidiesComponent.
 * This is exported only to be used in the above mentioned components.
 */
export default compose(withTranslation())(BaseCommissionsSubsidiesComponent);
